/**
 * @file AdminLayout.tsx
 * @description Master administration layout for TableGreet Venue Admin.
 * Provides unified navigation for both general venue operations and the
 * dedicated Reservations Studio, complete with structured navigation sections
 * and fluid Framer Motion transitions.
 */

import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Layers, 
  ShoppingBag, 
  Settings, 
  LogOut, 
  ChevronRight, 
  Menu as MenuIcon, 
  X, 
  QrCode, 
  Pizza, 
  Globe as GlobeIcon, 
  CreditCard, 
  Receipt,
  Users,
  History,
  Megaphone,
  Gift,
  Building,
  Clock,
  Shield,
  ChefHat,
  Palette,
  Heart,
  BarChart2,
  TvMinimalPlay,
  Lock,
  LifeBuoy,
  Bell,
  Sparkles,
  CalendarCheck,
  ArrowLeft,
  Armchair,
  Hourglass,
  UserPlus,
  Ticket,
  Calendar as CalendarIcon,
  Grid,
  Tag,
  ShieldAlert,
  Truck,
  Radio,
  MapPin
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.svg';
import { clsx } from 'clsx';
import { VenueSwitcher } from './admin/VenueSwitcher';
import { NotificationDropdown } from './admin/NotificationDropdown';
import { useVenues } from '../hooks/useVenues';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { useRoleAccess } from '../hooks/useRoleAccess';

import { LogoutModal } from './admin/LogoutModal';
import { OnboardingWidget } from './admin/OnboardingWidget';
import { supabase } from '../lib/supabase';

import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useAdminDomTranslations } from '../hooks/useAdminDomTranslations';
import { SUPPORTED_LANGUAGES, translateAdminPhrase } from '../lib/i18n';

interface NavItemConfig {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  labelKey: string;
  label: string;
  path: string;
  show?: boolean;
  locked?: boolean;
  exact?: boolean;
  badge?: string;
}

interface NavSectionConfig {
  title: string;
  titleKey: string;
  items: NavItemConfig[];
}

export const AdminLayout: React.FC = () => {
  useSessionTimeout(); // Enforces 30-minute session inactivity protection
  const { profile, activeVenue } = useVenues();
  const { t } = useTranslation();
  const { currentLanguage, setLanguage } = useLanguage();
  useAdminDomTranslations(currentLanguage);

  // Responsive sidebar state management
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(() => window.innerWidth >= 768);
  const [showLangMenu, setShowLangMenu] = React.useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);

  // Platform system broadcast announcement state
  const [broadcast, setBroadcast] = React.useState<{
    id: string;
    title: string;
    message: string;
    severity: 'info' | 'warning' | 'critical';
    dismissible: boolean;
  } | null>(null);

  // Active support impersonation session metadata
  const [supportSession] = React.useState<{
    session_id: string;
    employee_email: string;
    venue_name: string;
    reason: string;
    expires_at: string;
  } | null>(() => {
    try {
      const raw = sessionStorage.getItem('tablegreet_support_session');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.expires_at && new Date(parsed.expires_at).getTime() <= Date.now()) {
        sessionStorage.removeItem('tablegreet_support_session');
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  // Resolves active platform broadcast announcement from bootstrap state or edge function
  React.useEffect(() => {
    const fetchBroadcast = async () => {
      try {
        // Reads pre-seeded broadcast from unified bootstrap state to avoid redundant network invocations
        const cachedBroadcastRaw = sessionStorage.getItem('tablegreet_platform_broadcast');
        if (cachedBroadcastRaw) {
          try {
            const b = JSON.parse(cachedBroadcastRaw);
            if (b && (!b.expires_at || new Date(b.expires_at).getTime() > Date.now())) {
              const dismissedInDb = Array.isArray(activeVenue?.theme_settings?.dismissed_broadcasts) &&
                activeVenue.theme_settings.dismissed_broadcasts.includes(b.id);
              const dismissedInLocal = localStorage.getItem(`dismissed_broadcast_${b.id}`) === 'true';
              if (!dismissedInDb && !dismissedInLocal) {
                setBroadcast(b);
                return;
              }
            }
          } catch {
            // Suppresses malformed broadcast parsing errors
          }
        }

        const { data } = await supabase.functions.invoke('platform-admin', {
          body: { action: 'get_system_broadcast' },
        });
        if (data?.ok && data?.broadcast) {
          const b = data.broadcast;
          const dismissedInDb = Array.isArray(activeVenue?.theme_settings?.dismissed_broadcasts) &&
            activeVenue.theme_settings.dismissed_broadcasts.includes(b.id);
          const dismissedInLocal = localStorage.getItem(`dismissed_broadcast_${b.id}`) === 'true';
          if (!dismissedInDb && !dismissedInLocal) {
            setBroadcast(b);
          }
        }
      } catch {
        // Edge function or network offline - non-critical
      }
    };
    void fetchBroadcast();
  }, [activeVenue?.id]);

  // Synchronize broadcast dismissal state when venue context loads or changes
  React.useEffect(() => {
    if (broadcast && activeVenue?.theme_settings?.dismissed_broadcasts?.includes(broadcast.id)) {
      setBroadcast(null);
    }
  }, [activeVenue?.theme_settings?.dismissed_broadcasts, broadcast]);

  const handleDismissBroadcast = (broadcastId: string) => {
    localStorage.setItem(`dismissed_broadcast_${broadcastId}`, 'true');
    setBroadcast(null);
    if (activeVenue?.id) {
      const currentDismissed = activeVenue.theme_settings?.dismissed_broadcasts || [];
      if (!currentDismissed.includes(broadcastId)) {
        const updated = [...currentDismissed, broadcastId];
        activeVenue.theme_settings = {
          ...activeVenue.theme_settings,
          dismissed_broadcasts: updated
        };
        supabase
          .from('venues')
          .update({
            theme_settings: activeVenue.theme_settings
          })
          .eq('id', activeVenue.id)
          .then(() => {});
      }
    }
  };

  const handleExitSupport = () => {
    sessionStorage.removeItem('tablegreet_support_session');
    localStorage.removeItem('lastVenueId');
    window.location.href = '/admin';
  };

  const location = useLocation();

  // Closes drawer on mobile viewport navigation transitions
  React.useEffect(() => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  const { 
    isManagement, 
    role, 
    orgRole,
    canViewAnalytics, 
    canAccessSettings, 
    canManageTeam,
    canAccessLoyalty,
    canAccessAuditLogs,
    canAccessAPI,
    canAccessSSO,
    isMaxPlus,
    canAccessReservations,
    canAccessStaffDash,
    isProPlus,
    canAccessSignage,
    canAccessServiceHours,
    canAccessDepartments,
    canAccessCategories,
    canAccessSEO
  } = useRoleAccess();

  // Flags whether the user is operating within the Reservations Studio domain
  const isReservations = location.pathname.startsWith('/admin/reservations');

  // Flags whether the user is operating within the Delivery & Dispatch domain (dev environment only)
  const isDelivery = Boolean(import.meta.env.DEV) && location.pathname.startsWith('/admin/delivery');

  // Flags whether the user is operating within the Loyalty Program domain
  const isLoyalty = location.pathname.startsWith('/admin/loyalty');

  // Structured navigation groupings for standard venue administration
  const MAIN_NAV_SECTIONS: NavSectionConfig[] = [
    {
      title: 'Core & Operations',
      titleKey: 'admin.nav.core_revenue',
      items: [
        { icon: LayoutDashboard, labelKey: 'admin.nav.intelligence', label: 'Intelligence Studio', path: '/admin', show: canViewAnalytics, locked: false, exact: true },
        { icon: Truck, labelKey: 'admin.nav.delivery', label: 'Delivery & Dispatch', path: '/admin/delivery', show: Boolean(import.meta.env.DEV), locked: false },
        { icon: CalendarCheck, labelKey: 'admin.nav.reservations', label: 'Reservations', path: '/admin/reservations', show: true, locked: !canAccessReservations },
        { icon: ChefHat, labelKey: 'admin.nav.kitchen', label: 'Kitchen Ops', path: '/admin/kitchen', show: canAccessStaffDash || isManagement, locked: !canAccessStaffDash },
        { icon: BarChart2, labelKey: 'admin.nav.insights', label: 'Venue Insights', path: '/admin/insights', show: canViewAnalytics, locked: !isProPlus },
        { icon: Building, labelKey: 'admin.nav.departments', label: 'Departments', path: '/admin/departments', show: true, locked: !canAccessDepartments },
        { icon: Clock, labelKey: 'admin.nav.service_hours', label: 'Service Hours', path: '/admin/service-hours', show: true, locked: !canAccessServiceHours },
      ]
    },
    {
      title: 'Menu & Experience',
      titleKey: 'admin.nav.menu_experience',
      items: [
        { icon: Layers, labelKey: 'admin.nav.categories', label: 'Categories', path: '/admin/categories', show: true, locked: !canAccessCategories },
        { icon: ShoppingBag, labelKey: 'admin.nav.items', label: 'Menu Items', path: '/admin/items', show: true, locked: false },
        { icon: Pizza, labelKey: 'admin.nav.ingredients', label: 'Ingredients', path: '/admin/ingredients', show: true, locked: false },
        { icon: Heart, labelKey: 'admin.nav.loyalty', label: 'Loyalty Program', path: '/admin/loyalty', show: isManagement, locked: !canAccessLoyalty },
        { icon: Megaphone, labelKey: 'admin.nav.campaigns', label: 'Campaign Studio', path: '/admin/campaigns', show: isManagement, locked: !isProPlus },
        { icon: TvMinimalPlay, labelKey: 'admin.nav.signage', label: 'Signage', path: '/admin/signage', show: true, locked: !canAccessSignage },
        { icon: QrCode, labelKey: 'admin.nav.qr', label: 'QR Code', path: '/admin/qr', show: true, locked: false },
        { icon: Palette, labelKey: 'admin.nav.theme', label: 'Theme Engine', path: '/admin/theme', show: true, locked: false },
      ]
    },
    {
      title: 'Enterprise & Security',
      titleKey: 'admin.nav.governance_security',
      items: [
        { icon: Building, labelKey: 'admin.nav.governance', label: 'Governance', show: isManagement, locked: !isMaxPlus, path: '/admin/governance' },
        { icon: History, labelKey: 'admin.nav.audit', label: 'Audit Log', path: '/admin/audit', show: isManagement, locked: !canAccessAuditLogs },
        { icon: GlobeIcon, labelKey: 'admin.nav.api', label: 'API & Webhooks', path: '/admin/api', show: isManagement, locked: !canAccessAPI },
        { icon: Shield, labelKey: 'admin.nav.sso', label: 'SSO & Security', path: '/admin/sso', show: isManagement, locked: !canAccessSSO },
        { icon: Users, labelKey: 'admin.nav.team', label: 'Team', path: '/admin/team', show: canManageTeam, locked: false },
        { icon: CreditCard, labelKey: 'admin.nav.billing', label: 'Billing', path: '/admin/billing', show: isManagement, locked: false },
        { icon: Receipt, labelKey: 'admin.nav.tax', label: 'Tax & Compliance', path: '/admin/tax', show: isManagement, locked: false },
        { icon: GlobeIcon, labelKey: 'admin.nav.seo', label: 'SEO & Google', path: '/admin/seo', show: isManagement || orgRole === 'ADMIN' || role === 'ADMIN', locked: !canAccessSEO },
      ]
    },
    {
      title: 'System & Control',
      titleKey: 'admin.nav.system_preferences',
      items: [
        { icon: Bell, labelKey: 'admin.nav.notifications', label: 'Notifications', path: '/admin/notifications', show: true, locked: false },
        { icon: LifeBuoy, labelKey: 'admin.nav.support', label: 'Support Center', path: '/admin/support', show: true, locked: false },
        { icon: Settings, labelKey: 'admin.nav.settings', label: 'Settings', path: '/admin/settings', show: canAccessSettings, locked: false },
      ]
    }
  ];

  // Structured navigation groupings for TableGreet Reservation Studio
  const RESERVATION_NAV_SECTIONS: NavSectionConfig[] = [
    {
      title: 'Operations',
      titleKey: 'admin.nav.operations',
      items: [
        { icon: Sparkles, labelKey: 'admin.nav.res_overview', label: 'Overview', path: '/admin/reservations', exact: true },
        { icon: Clock, labelKey: 'admin.nav.res_timeline', label: 'Timeline', path: '/admin/reservations/timeline' },
        { icon: CalendarIcon, labelKey: 'admin.nav.res_calendar', label: 'Calendar', path: '/admin/reservations/calendar' },
        { icon: Armchair, labelKey: 'admin.nav.res_floor_plan', label: 'Floor Plan', path: '/admin/reservations/floor-plan' },
        { icon: Hourglass, labelKey: 'admin.nav.res_waitlist', label: 'Live Waitlist', path: '/admin/reservations/waitlist' },
        { icon: UserPlus, labelKey: 'admin.nav.res_walk_ins', label: 'Walk-Ins', path: '/admin/reservations/walk-ins' },
        { icon: CalendarCheck, labelKey: 'admin.nav.res_list', label: 'Reservations List', path: '/admin/reservations/list' },
      ]
    },
    {
      title: 'Guests & CRM',
      titleKey: 'admin.nav.guests',
      items: [
        { icon: Users, labelKey: 'admin.nav.res_guests', label: 'Customers CRM', path: '/admin/reservations/guests' },
      ]
    },
    {
      title: 'Events',
      titleKey: 'admin.nav.events',
      items: [
        { icon: Ticket, labelKey: 'admin.nav.res_events', label: 'Special Events', path: '/admin/reservations/events' },
      ]
    },
    {
      title: 'Configuration',
      titleKey: 'admin.nav.configuration',
      items: [
        { icon: Settings, labelKey: 'admin.nav.res_settings', label: 'Turn Times & General', path: '/admin/reservations/settings', exact: true },
        { icon: Grid, labelKey: 'admin.nav.res_tables', label: 'Tables & Sections', path: '/admin/reservations/settings/tables' },
        { icon: Tag, labelKey: 'admin.nav.res_booking_types', label: 'Booking Types & Meal Periods', path: '/admin/reservations/settings/booking-types' },
        { icon: ShieldAlert, labelKey: 'admin.nav.res_policies', label: 'Policies & Deposits', path: '/admin/reservations/settings/policies' },
      ]
    }
  ];

  // Structured navigation groupings for TableGreet Delivery & Dispatch Workspace
  const DELIVERY_NAV_SECTIONS: NavSectionConfig[] = [
    {
      title: 'Operations & Dispatch',
      titleKey: 'admin.nav.delivery_ops',
      items: [
        { icon: LayoutDashboard, labelKey: 'admin.nav.delivery_overview', label: 'Delivery Overview', path: '/admin/delivery', exact: true },
        { icon: ShoppingBag, labelKey: 'admin.nav.delivery_orders', label: 'Delivery Orders', path: '/admin/delivery/orders' },
        { icon: Radio, labelKey: 'admin.nav.delivery_dispatch', label: 'Live Dispatch Radar', path: '/admin/delivery/dispatch' },
      ]
    },
    {
      title: 'Fleet & Coverage',
      titleKey: 'admin.nav.delivery_fleet',
      items: [
        { icon: Truck, labelKey: 'admin.nav.delivery_drivers', label: 'Couriers & Fleet', path: '/admin/delivery/drivers' },
        { icon: MapPin, labelKey: 'admin.nav.delivery_zones', label: 'Delivery Zones', path: '/admin/delivery/zones' },
      ]
    },
    {
      title: 'Growth & Analytics',
      titleKey: 'admin.nav.delivery_growth',
      items: [
        { icon: Users, labelKey: 'admin.nav.delivery_customers', label: 'Delivery Customers', path: '/admin/delivery/customers' },
        { icon: Megaphone, labelKey: 'admin.nav.delivery_promotions', label: 'Promotions & Deals', path: '/admin/delivery/promotions' },
        { icon: BarChart2, labelKey: 'admin.nav.delivery_analytics', label: 'Performance & KPIs', path: '/admin/delivery/analytics' },
      ]
    },
    {
      title: 'Configuration',
      titleKey: 'admin.nav.delivery_config',
      items: [
        { icon: Settings, labelKey: 'admin.nav.delivery_settings', label: 'Delivery Settings', path: '/admin/delivery/settings' },
      ]
    }
  ];

  // Structured navigation groupings for TableGreet Loyalty Program
  const LOYALTY_NAV_SECTIONS: NavSectionConfig[] = [
    {
      title: 'Program & Rewards',
      titleKey: 'admin.nav.loyalty_program',
      items: [
        { icon: LayoutDashboard, labelKey: 'admin.nav.loyalty_overview', label: 'Overview', path: '/admin/loyalty', exact: true },
        { icon: Settings, labelKey: 'admin.nav.loyalty_settings', label: 'Program Settings', path: '/admin/loyalty/settings' },
        { icon: Gift, labelKey: 'admin.nav.loyalty_rewards', label: 'Rewards Catalog', path: '/admin/loyalty/rewards' },
      ]
    },
    {
      title: 'Members & Activity',
      titleKey: 'admin.nav.loyalty_members',
      items: [
        { icon: Users, labelKey: 'admin.nav.loyalty_members_dir', label: 'Member Directory', path: '/admin/loyalty/members' },
        { icon: History, labelKey: 'admin.nav.loyalty_tx', label: 'Transaction Ledger', path: '/admin/loyalty/transactions' },
      ]
    },
    {
      title: 'Growth & Insights',
      titleKey: 'admin.nav.loyalty_growth',
      items: [
        { icon: Megaphone, labelKey: 'admin.nav.loyalty_campaigns', label: 'Campaign Studio', path: '/admin/loyalty/campaigns' },
        { icon: BarChart2, labelKey: 'admin.nav.loyalty_analytics', label: 'Program Analytics', path: '/admin/loyalty/analytics' },
      ]
    }
  ];

  // Aggregates flat items to resolve the active page header and icon
  const currentSections = isDelivery 
    ? DELIVERY_NAV_SECTIONS 
    : (isReservations ? RESERVATION_NAV_SECTIONS : (isLoyalty ? LOYALTY_NAV_SECTIONS : MAIN_NAV_SECTIONS));
  const allCurrentNavItems = currentSections.flatMap(s => s.items.filter(i => i.show !== false));

  const currentNavItem = (isDelivery || isReservations)
    ? (allCurrentNavItems.find(item => item.exact ? location.pathname === item.path : (item.path !== '/admin' && location.pathname.startsWith(item.path))) || allCurrentNavItems[0])
    : (allCurrentNavItems.find(item => item.exact ? location.pathname === item.path : (item.path !== '/admin' && location.pathname.startsWith(item.path))) || allCurrentNavItems[0]);

  const PageIcon = currentNavItem?.icon || LayoutDashboard;
  const pageLabel = translateAdminPhrase(currentLanguage, currentNavItem?.label || 'Dashboard');
  
  // Mapping of route slugs to informative descriptive subtitles (hospitality-focused)
  const SUBTITLE_MAP: Record<string, { key: string; fallback: string }> = {
    'admin': { key: 'admin.subtitle.admin', fallback: 'Venue Overview' },
    'reservations': { key: 'admin.subtitle.reservations', fallback: 'Table & Guest Workspace' },
    'delivery': { key: 'admin.subtitle.delivery', fallback: 'Delivery Operations & Fleet' },
    'dispatch': { key: 'admin.subtitle.dispatch', fallback: 'Realtime Dispatch Radar' },
    'drivers': { key: 'admin.subtitle.drivers', fallback: 'Couriers & Fleet Roster' },
    'zones': { key: 'admin.subtitle.zones', fallback: 'Delivery Coverage Zones' },
    'orders': { key: 'admin.subtitle.delivery_orders', fallback: 'Delivery Orders Fulfillment' },
    'timeline': { key: 'admin.subtitle.timeline', fallback: 'Service Schedule' },
    'calendar': { key: 'admin.subtitle.calendar', fallback: 'Monthly Bookings & Calendar' },
    'floor-plan': { key: 'admin.subtitle.floor_plan', fallback: 'Table Layout & Status' },
    'list': { key: 'admin.subtitle.res_list', fallback: 'All Reservations' },
    'waitlist': { key: 'admin.subtitle.waitlist', fallback: 'Waiting List' },
    'walk-ins': { key: 'admin.subtitle.walk_ins', fallback: 'Walk-in Guests' },
    'guests': { key: 'admin.subtitle.guests', fallback: 'Guest Profiles & History' },
    'customers': { key: 'admin.subtitle.guests', fallback: 'Customer Directory' },
    'events': { key: 'admin.subtitle.events', fallback: 'Special Dining & Events' },
    'tables': { key: 'admin.subtitle.tables', fallback: 'Tables & Seating Areas' },
    'sections': { key: 'admin.subtitle.tables', fallback: 'Dining Rooms & Sections' },
    'booking-types': { key: 'admin.subtitle.booking_types', fallback: 'Meal Times & Booking Options' },
    'policies': { key: 'admin.subtitle.policies', fallback: 'Dining Times & Cancellation Rules' },
    'notifications': { key: 'admin.subtitle.notifications', fallback: 'Notifications & Alerts' },
    'insights': { key: 'admin.subtitle.insights', fallback: 'Sales & Performance Reports' },
    'departments': { key: 'admin.subtitle.departments', fallback: 'Venue Areas & Menus' },
    'service-hours': { key: 'admin.subtitle.service-hours', fallback: 'Opening & Serving Hours' },
    'categories': { key: 'admin.subtitle.categories', fallback: 'Menu Categories' },
    'items': { key: 'admin.subtitle.items', fallback: 'Menu Items & Availability' },
    'ingredients': { key: 'admin.subtitle.ingredients', fallback: 'Ingredients & Allergens' },
    'campaigns': { key: 'admin.subtitle.campaigns', fallback: 'Special Offers & Promotions' },
    'signage': { key: 'admin.subtitle.signage', fallback: 'TV Menu Displays' },
    'qr': { key: 'admin.subtitle.qr', fallback: 'QR Codes & Table Stands' },
    'theme': { key: 'admin.subtitle.theme', fallback: 'Menu Colors & Styling' },
    'audit': { key: 'admin.subtitle.audit', fallback: 'Activity & Change History' },
    'team': { key: 'admin.subtitle.team', fallback: 'Staff Members & Roles' },
    'billing': { key: 'admin.subtitle.billing', fallback: 'Subscription & Invoices' },
    'settings': { key: 'admin.subtitle.settings', fallback: 'Restaurant Settings' },
    'general': { key: 'admin.subtitle.settings', fallback: 'Venue Information' },
    'guest': { key: 'admin.subtitle.settings', fallback: 'Guest Ordering Controls' },
    'security': { key: 'admin.subtitle.sso', fallback: 'Account Security & Login' },
    'domain': { key: 'admin.subtitle.settings', fallback: 'Custom Web Address' },
    'hours': { key: 'admin.subtitle.service-hours', fallback: 'Operating Hours' },
    'health': { key: 'admin.subtitle.settings', fallback: 'Setup Progress' },
    'branding': { key: 'admin.subtitle.theme', fallback: 'Logos & Colors' },
    'loyalty': { key: 'admin.subtitle.loyalty', fallback: 'Customer Rewards & Points' },
    'rewards': { key: 'admin.subtitle.loyalty_rewards', fallback: 'Rewards Catalog' },
    'members': { key: 'admin.subtitle.loyalty_members', fallback: 'Member Directory' },
    'transactions': { key: 'admin.subtitle.loyalty_tx', fallback: 'Transaction Ledger' },
    'governance': { key: 'admin.subtitle.governance', fallback: 'Multiple Locations' },
    'api': { key: 'admin.subtitle.api', fallback: 'Integrations & Connections' },
    'sso': { key: 'admin.subtitle.sso', fallback: 'Single Sign-On & Security' },
    'support': { key: 'admin.subtitle.support', fallback: 'Help & Support' },
    'seo': { key: 'admin.subtitle.seo', fallback: 'Google & Search Visibility' }
  };

  const pageKey = location.pathname.split('/').pop() || 'admin';
  const subtitle = SUBTITLE_MAP[pageKey] || { key: 'admin.subtitle.default', fallback: 'Management Console' };
  const pageSubtitle = translateAdminPhrase(currentLanguage, subtitle.fallback);

  // Renders an individual navigation link with spring-animated active pill
  const renderNavItem = (item: NavItemConfig, scope: string) => {
    const isActive = item.exact
      ? location.pathname === item.path
      : item.path === '/admin'
      ? location.pathname === '/admin'
      : location.pathname.startsWith(item.path);

    return (
      <motion.div
        key={item.path}
        whileHover={{ x: isSidebarOpen ? 2 : 0 }}
        whileTap={{ scale: 0.98 }}
        className="w-full relative"
      >
        <Link
          to={item.path}
          title={!isSidebarOpen ? translateAdminPhrase(currentLanguage, item.label) : undefined}
          className={clsx(
            "admin-nav-link relative flex items-center transition-colors group touch-manipulation select-none",
            isSidebarOpen 
              ? "w-full min-h-10 gap-3 px-3 py-2 rounded-xl justify-start" 
              : "w-11 h-11 justify-center rounded-xl mx-auto p-0 my-0.5",
            isActive 
              ? "text-white font-semibold" 
              : "text-gray-400 hover:text-white hover:bg-white/5"
          )}
        >
          {/* Fluid gliding background pill matching active state */}
          {isActive && (
            <motion.div
              layoutId={`${scope}-active-pill`}
              className="absolute inset-0 bg-brand-primary rounded-xl shadow-lg shadow-brand-primary/25 -z-0"
              transition={{ type: "spring", stiffness: 450, damping: 35 }}
            />
          )}

          <item.icon
            size={18}
            className={clsx(
              "relative z-10 shrink-0 transition-transform group-hover:scale-105",
              isActive ? "text-white" : "group-hover:text-brand-primary text-gray-400"
            )}
          />

          {isSidebarOpen && (
            <span className="relative z-10 font-medium text-xs tracking-tight truncate">
              {translateAdminPhrase(currentLanguage, item.label)}
            </span>
          )}

          {item.badge && isSidebarOpen && (
            <span className="relative z-10 ml-auto text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
              {item.badge}
            </span>
          )}

          {item.locked ? (
            isSidebarOpen && (
              <Lock size={12} className="relative z-10 ml-auto text-gray-500 group-hover:text-brand-primary/80 transition-colors" />
            )
          ) : (
            isActive && isSidebarOpen && !item.badge && (
              <ChevronRight size={14} className="relative z-10 ml-auto opacity-60" />
            )
          )}
        </Link>
      </motion.div>
    );
  };

  return (
    <div className="admin-shell h-screen bg-brand-dark text-white flex flex-col overflow-hidden">
      {/* TableGreet Internal Support Impersonation Session Banner */}
      {supportSession && (
        <div className="w-full bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white px-4 py-2 text-xs font-semibold flex items-center justify-between border-b border-amber-500/30 z-50 shrink-0 shadow-lg">
          <div className="flex items-center gap-2.5 min-w-0">
            <ShieldAlert size={16} className="text-amber-200 shrink-0 animate-pulse" />
            <span className="truncate">
              <strong className="font-bold">TableGreet Support Session Active</strong>
              <span className="opacity-90"> — Viewing as Support Specialist ({supportSession.employee_email}) for "{supportSession.venue_name}". Reason: "{supportSession.reason}"</span>
            </span>
          </div>
          <button
            onClick={handleExitSupport}
            className="shrink-0 ml-4 px-3 py-1 bg-black/40 hover:bg-black/60 rounded-lg text-amber-200 hover:text-white border border-white/20 transition-all font-mono text-[11px] font-bold cursor-pointer"
          >
            Exit Support Mode
          </button>
        </div>
      )}

      {/* Platform-Wide System Announcement Banner */}
      {broadcast && (
        <div
          className={clsx(
            "w-full px-4 py-2 text-xs font-medium flex items-center justify-between border-b z-40 shrink-0 shadow-sm transition-all",
            broadcast.severity === 'critical' && "bg-rose-950/90 text-rose-200 border-rose-800/60",
            broadcast.severity === 'warning' && "bg-amber-950/90 text-amber-200 border-amber-800/60",
            broadcast.severity === 'info' && "bg-blue-950/90 text-blue-200 border-blue-800/60"
          )}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Megaphone size={15} className="shrink-0 opacity-80" />
            <span className="truncate">
              <strong className="font-bold mr-1.5">{broadcast.title}:</strong>
              <span>{broadcast.message}</span>
            </span>
          </div>
          {broadcast.dismissible && (
            <button
              onClick={() => handleDismissBroadcast(broadcast.id)}
              className="shrink-0 ml-4 p-1 hover:bg-white/10 rounded transition-colors text-white/70 hover:text-white cursor-pointer"
              aria-label="Dismiss Announcement"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Session Termination Confirmation Modal */}
        <LogoutModal 
          isOpen={isLogoutModalOpen} 
          onClose={() => setIsLogoutModalOpen(false)} 
        />

        {/* Primary Sidebar Surface */}
        <aside 
        className={clsx(
          "fixed md:relative z-50 h-full transition-all duration-300 border-r border-white/5 bg-brand-surface",
          isSidebarOpen ? "w-64 translate-x-0" : "w-20 -translate-x-full md:translate-x-0"
        )}
      >
        <div className={clsx(
          "flex flex-col h-full",
          isSidebarOpen ? "p-4" : "py-4 px-2"
        )}>
          {/* Brand Header & Toggle */}
          <div className={clsx(
            "flex items-center mb-6 px-2",
            isSidebarOpen ? "justify-between" : "justify-center w-full"
          )}>
            <div className={clsx("flex items-center gap-3 truncate", !isSidebarOpen && "hidden")}>
              <img src={logo} className="w-8 h-8 object-contain" alt="TableGreet™" />
              <span className="font-bold text-lg tracking-tight bg-gradient-to-r from-brand-primary to-orange-400 bg-clip-text text-transparent">
                TableGreet<sup className="text-[0.5em] font-bold align-super">™</sup>
              </span>
            </div>
            <Button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              variant="ghost"
              size="icon"
              className="hover:bg-white/5"
              leftIcon={isSidebarOpen ? X : MenuIcon}
            />
          </div>

          {/* Navigation Container with Smooth Transition Animations */}
          <nav className="admin-sidebar-nav flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar pr-1 touch-pan-y">
            <AnimatePresence mode="wait" initial={false}>
              {isDelivery ? (
                <motion.div
                  key="delivery-nav-group"
                  initial={{ opacity: 0, x: 10, filter: 'blur(3px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -10, filter: 'blur(3px)' }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-4"
                >
                  {/* Return to Core Admin HQ Button */}
                  <div className="px-1">
                    <Link
                      to="/admin"
                      className={clsx(
                        "flex items-center gap-2.5 rounded-xl bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary border border-brand-primary/20 transition-all group font-semibold text-xs shadow-sm",
                        isSidebarOpen ? "w-full p-2.5 justify-start" : "w-11 h-11 mx-auto justify-center p-0"
                      )}
                      title={!isSidebarOpen ? "Exit to Main Admin" : undefined}
                    >
                      <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform text-brand-primary shrink-0" />
                      {isSidebarOpen && <span className="text-white font-medium">{translateAdminPhrase(currentLanguage, 'Exit to Main Admin')}</span>}
                    </Link>
                  </div>

                  {/* Section Groups */}
                  {DELIVERY_NAV_SECTIONS.map((section, sIdx) => (
                    <div key={section.title} className="space-y-1">
                      {isSidebarOpen ? (
                        <p className="px-3 pt-2 pb-1 text-[10px] font-black uppercase tracking-wider text-gray-500 select-none">
                          {translateAdminPhrase(currentLanguage, section.title)}
                        </p>
                      ) : (
                        sIdx > 0 && <div className="my-2 border-t border-white/5 mx-2" />
                      )}
                      {section.items.map((item) => renderNavItem(item, 'del'))}
                    </div>
                  ))}
                </motion.div>
              ) : isReservations ? (
                <motion.div
                  key="reservations-nav-group"
                  initial={{ opacity: 0, x: 10, filter: 'blur(3px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -10, filter: 'blur(3px)' }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-4"
                >
                  {/* Return to Core Admin HQ Button */}
                  <div className="px-1">
                    <Link
                      to="/admin"
                      className={clsx(
                        "flex items-center gap-2.5 rounded-xl bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary border border-brand-primary/20 transition-all group font-semibold text-xs shadow-sm",
                        isSidebarOpen ? "w-full p-2.5 justify-start" : "w-11 h-11 mx-auto justify-center p-0"
                      )}
                      title={!isSidebarOpen ? "Exit to Main Admin" : undefined}
                    >
                      <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform text-brand-primary shrink-0" />
                      {isSidebarOpen && <span className="text-white font-medium">{translateAdminPhrase(currentLanguage, 'Exit to Main Admin')}</span>}
                    </Link>
                  </div>

                  {/* Section Groups */}
                  {RESERVATION_NAV_SECTIONS.map((section, sIdx) => (
                    <div key={section.title} className="space-y-1">
                      {isSidebarOpen ? (
                        <p className="px-3 pt-2 pb-1 text-[10px] font-black uppercase tracking-wider text-gray-500 select-none">
                          {translateAdminPhrase(currentLanguage, section.title)}
                        </p>
                      ) : (
                        sIdx > 0 && <div className="my-2 border-t border-white/5 mx-2" />
                      )}
                      {section.items.map((item) => renderNavItem(item, 'res'))}
                    </div>
                  ))}
                </motion.div>
              ) : isLoyalty ? (
                <motion.div
                  key="loyalty-nav-group"
                  initial={{ opacity: 0, x: 10, filter: 'blur(3px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -10, filter: 'blur(3px)' }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-4"
                >
                  {/* Return to Core Admin HQ Button */}
                  <div className="px-1">
                    <Link
                      to="/admin"
                      className={clsx(
                        "flex items-center gap-2.5 rounded-xl bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary border border-brand-primary/20 transition-all group font-semibold text-xs shadow-sm",
                        isSidebarOpen ? "w-full p-2.5 justify-start" : "w-11 h-11 mx-auto justify-center p-0"
                      )}
                      title={!isSidebarOpen ? "Exit to Main Admin" : undefined}
                    >
                      <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform text-brand-primary shrink-0" />
                      {isSidebarOpen && <span className="text-white font-medium">{translateAdminPhrase(currentLanguage, 'Exit to Main Admin')}</span>}
                    </Link>
                  </div>

                  {/* Section Groups */}
                  {LOYALTY_NAV_SECTIONS.map((section, sIdx) => (
                    <div key={section.title} className="space-y-1">
                      {isSidebarOpen ? (
                        <p className="px-3 pt-2 pb-1 text-[10px] font-black uppercase tracking-wider text-gray-500 select-none">
                          {translateAdminPhrase(currentLanguage, section.title)}
                        </p>
                      ) : (
                        sIdx > 0 && <div className="my-2 border-t border-white/5 mx-2" />
                      )}
                      {section.items.map((item) => renderNavItem(item, 'loy'))}
                    </div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="main-nav-group"
                  initial={{ opacity: 0, x: -10, filter: 'blur(3px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: 10, filter: 'blur(3px)' }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-4"
                >
                  {MAIN_NAV_SECTIONS.map((section, sIdx) => {
                    const visibleItems = section.items.filter(item => item.show !== false);
                    if (visibleItems.length === 0) return null;

                    return (
                      <div key={section.title} className="space-y-1">
                        {isSidebarOpen ? (
                          <p className="px-3 pt-2 pb-1 text-[10px] font-black uppercase tracking-wider text-gray-500 select-none">
                            {translateAdminPhrase(currentLanguage, section.title)}
                          </p>
                        ) : (
                          sIdx > 0 && <div className="my-2 border-t border-white/5 mx-2" />
                        )}
                        {visibleItems.map((item) => renderNavItem(item, 'main'))}
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </nav>

          {/* Session Logout Action */}
          <Button 
            onClick={() => setIsLogoutModalOpen(true)}
            variant="ghost"
            className={clsx(
              "mt-4 flex items-center text-gray-500 hover:text-red-400 hover:bg-red-400/10 border-transparent hover:border-red-400/20 transition-all",
              isSidebarOpen ? "justify-start p-3 w-full" : "justify-center mx-auto w-11 h-11 p-0 rounded-xl"
            )}
            leftIcon={LogOut}
          >
            {isSidebarOpen && <span className="font-bold text-sm tracking-tight uppercase">{translateAdminPhrase(currentLanguage, 'Sign Out')}</span>}
          </Button>
        </div>
      </aside>

      {/* Main Administrative Stage */}
      <main className="flex-1 flex flex-col min-w-0 bg-brand-dark">
        {/* Top Control Bar */}
        <header className="admin-header h-20 border-b border-white/5 flex items-center justify-between gap-4 px-4 md:px-8 bg-brand-surface sticky top-0 z-40">
          <div className="flex min-w-0 flex-1 items-center gap-4 xl:gap-6">
            <VenueSwitcher />
            <div className="h-8 w-px bg-white/10 hidden sm:block" />
            
            {/* Animated Header Title & Subtitle */}
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
                className="hidden min-w-0 md:flex items-center gap-4"
              >
                <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center text-brand-primary border border-brand-primary/20 shrink-0">
                  <PageIcon size={20} />
                </div>
                <div className="min-w-0">
                  <h1 className="truncate text-lg font-bold text-white tracking-tight leading-tight capitalize">
                    {pageLabel}
                  </h1>
                  <p className="truncate text-[10px] text-gray-500 font-black uppercase tracking-widest leading-none mt-1">
                    {pageSubtitle}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          
          <div className="flex shrink-0 items-center gap-3 xl:gap-5">
            {/* Operational Alert Center */}
            <NotificationDropdown />

            {/* Internationalization Selector */}
            <div className="relative" data-no-translate data-no-admin-i18n>
              <Button 
                onClick={() => setShowLangMenu(!showLangMenu)}
                aria-label={translateAdminPhrase(currentLanguage, 'Change Language')}
                variant="glass"
                size="sm"
                className="gap-2 px-3 py-2 border-white/10"
                leftIcon={GlobeIcon}
              >
                <span className="text-[10px] font-black uppercase text-gray-300 font-mono tracking-wider">{currentLanguage}</span>
              </Button>
              {showLangMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowLangMenu(false)} 
                  />
                  <div className="absolute right-0 mt-2 w-52 bg-brand-surface border border-white/10 rounded-2xl shadow-2xl z-20 py-2 max-h-72 overflow-y-auto custom-scrollbar animate-in fade-in zoom-in duration-200">
                    <div className="px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-gray-500 border-b border-white/5 mb-1">
                      {translateAdminPhrase(currentLanguage, 'Change Language')}
                    </div>
                    {SUPPORTED_LANGUAGES.map((lang) => {
                      const isSelected = currentLanguage === lang.code;
                      return (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setShowLangMenu(false);
                          }}
                          className={clsx(
                            "w-full min-h-10 px-3.5 py-2 text-xs flex items-center justify-between hover:bg-brand-primary/10 transition-colors touch-manipulation",
                            currentLanguage === 'ar' ? "text-right flex-row-reverse" : "text-left",
                            isSelected ? "text-brand-primary font-bold bg-brand-primary/5" : "text-gray-300 hover:text-white"
                          )}
                        >
                          <div className="flex items-center gap-2 truncate">
                            <span className="font-medium truncate">{lang.nativeLabel}</span>
                            {lang.label !== lang.nativeLabel && (
                              <span className="text-[10px] text-gray-500 font-normal truncate">({lang.label})</span>
                            )}
                          </div>
                          <span className="text-[9px] uppercase font-mono px-1.5 py-0.5 rounded bg-white/5 text-gray-400 font-bold ml-2 shrink-0">
                            {lang.code}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Profile Avatar Badge */}
            <div className="hidden max-w-[220px] min-w-0 flex-col items-end lg:flex">
              <span className="max-w-full truncate text-sm font-bold text-white">{profile?.full_name || t('admin.user.administrator', 'Administrator')}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge 
                  variant={isManagement ? "brand" : role === 'VIEWER' ? "gray" : "warning"} 
                  size="sm" 
                  dot
                >
                  {role || 'ADMIN'}
                </Badge>
                <span className="truncate text-[9px] text-gray-500 uppercase font-black tracking-widest">
                  {isManagement ? t('admin.user.global_admin', 'Global Admin') : t('admin.user.staff_access', 'Staff Access')}
                </span>
              </div>
            </div>
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-primary to-orange-500 p-[1px]">
              <div className="w-full h-full rounded-[15px] bg-brand-surface flex items-center justify-center text-brand-primary font-black text-lg border border-white/10">
                {profile?.full_name?.charAt(0) || 'A'}
              </div>
            </div>
          </div>
        </header>

        {/* View Surface: Suspense boundary ensures lazy route chunks load reliably without getting frozen by animation exit states */}
        <div className="admin-content flex-1 overflow-auto custom-scrollbar bg-brand-dark touch-pan-y">
          <React.Suspense fallback={
            <div className="flex-1 min-h-[400px] flex flex-col items-center justify-center gap-3">
              <div className="w-8 h-8 border-2 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
            </div>
          }>
            <Outlet />
          </React.Suspense>
        </div>
      </main>
    </div>

      {/* Floating Onboarding Widget */}
      <OnboardingWidget />

      {/* Mobile Backdrop Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};
